import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Icon } from "@iconify/react";
import moment from "moment";
import "moment/locale/th";

moment.locale("th");

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
  headers: { "Content-Type": "application/json", key: "THUNDER1WoV5UbrGPq5iOXS2SS4ODR9999" },
});

const formatDate = (d) => {
  if (!d) return "-";
  const m = moment(d);
  return m.isValid() ? m.format("D MMMM ") + (m.year() + 543) : "-";
};

const StarRating = ({ value, onChange, readonly }) => (
  <div className="flex gap-2">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        disabled={readonly}
        onClick={() => !readonly && onChange(star)}
        className={`text-5xl transition-transform ${readonly ? "cursor-default" : "cursor-pointer hover:scale-110"}`}
      >
        <Icon
          icon={star <= value ? "mdi:star" : "mdi:star-outline"}
          className={star <= value ? "text-yellow-400 !w-7 !h-7" : "text-gray-300 !w-7 !h-7"}
        />
      </button>
    ))}
  </div>
);

const starLabel = (r) => ["", "ควรปรับปรุง", "พอใช้", "ดี", "ดีมาก", "ยอดเยี่ยม"][r] || "";

export default function FeedbackPage() {
  const { token } = useParams();

  const [loading, setLoading]       = useState(true);
  const [notFound, setNotFound]     = useState(false);
  const [info, setInfo]             = useState(null);
  const [ratings, setRatings]       = useState({
    onTime: 0,
    cleanliness: 0,
    knowledge: 0,
    politeness: 0,
  });
  const [comment, setComment]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState("");
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get(`/app/public/feedback/${token}`)
      .then(async (res) => {
        if (res.data?.status === "OK") {
          const d = res.data.data;
          let signatureInfo = {
            signed: false,
            signatureImage: null,
            reason: null,
            signedBy: null,
            signedDate: null,
          };
          let serviceNames = Array.isArray(d.service_names) ? d.service_names.filter(Boolean) : [];
          let serviceLocation = d.service_location || null;
          let roundNo = null;
          let totalRounds = null;

          // fallback ข้อมูลหลักจาก calendar_id กรณี token เก่า/ข้อมูล snapshot ไม่ครบ
          if (d.calendar_id && (!d.contract_no || !d.customer_name || !d.contract_name || !serviceLocation || !d.contract_id)) {
            try {
              const assignedRes = await api.get("/get-assigned-jobs", {
                params: { calendar_id: d.calendar_id },
              });
              const assigned = Array.isArray(assignedRes.data?.result) ? assignedRes.data.result[0] : null;
              if (assigned) {
                d.contract_id = d.contract_id || assigned.JobId || null;
                d.contract_no = d.contract_no || assigned.Contract_No || null;
                d.customer_name = d.customer_name || assigned.CustomerName || null;
                d.contract_name = d.contract_name || assigned.ContractCustomerName || assigned.CustomerName || null;
                serviceLocation = serviceLocation || assigned.ServiceAddr_Name || null;
                d.job_date = d.job_date || assigned.Job_Date || null;
                d.time_start = d.time_start || assigned.Time_Start || null;
                d.time_end = d.time_end || assigned.Time_End || null;
                d.team_name = d.team_name || assigned.teamName || null;
              }
            } catch (_) {
              // ignore and continue with existing data
            }
          }

          // เติมที่อยู่เต็มจากข้อมูลสัญญา เพื่อให้มี ตำบล/อำเภอ/จังหวัด/ไปรษณีย์
          if (d.contract_id || d.doc_id) {
            try {
              const contractRefId = d.contract_id || d.doc_id;
              const contractRes = await api.get(`/get-contract/${contractRefId}`);
              const header = contractRes.data?.header || {};
              const works = Array.isArray(contractRes.data?.works) ? contractRes.data.works : [];
              totalRounds = works.length > 0 ? works.length : null;
              d.contract_no = d.contract_no || header.Doc_No || null;
              d.customer_name = d.customer_name || header.AR_Name || null;
              d.contract_name = d.contract_name || header.DisplayName || header.AR_Name || null;
              const addrParts = [
                header.AddrNo || null,
                header.Building || null,
                header.Village || null,
                header.Moo ? `หมู่ ${header.Moo}` : null,
                header.Soi ? `ซอย ${header.Soi}` : null,
                header.Road ? `ถนน ${header.Road}` : null,
                (header.SubDistrictName || header.SubDistrict) ? `แขวง/ตำบล ${header.SubDistrictName || header.SubDistrict}` : null,
                (header.DistrictName || header.District) ? `เขต/อำเภอ ${header.DistrictName || header.District}` : null,
                header.ProvinceName || header.Province || null,
                header.Zipcode || header.PostCode || null,
              ].filter(Boolean);
              const fullAddress = addrParts.length ? addrParts.join(" ") : null;
              const serviceAddrName = header.ServiceAddr_Name || serviceLocation || null;
              if (fullAddress) {
                serviceLocation = [serviceAddrName, fullAddress].filter(Boolean).join(" ").trim();
              } else {
                serviceLocation = serviceLocation || serviceAddrName;
              }
            } catch (_) {
              // ignore and keep current value
            }
          }

          if (d.calendar_id) {
            try {
              const closeRes = await api.get("/get-job-close-web", {
                params: { calendar_id: d.calendar_id },
              });
              const closeData = closeRes.data?.result || null;
              d.contract_id = d.contract_id || closeData?.job_id || null;
              roundNo = Number(closeData?.Round_No || 0) || null;
              const allServices = Array.isArray(closeData?.Services) ? closeData.Services : [];
              const liveServiceNames = allServices.map((s) => s?.Service_Name).filter(Boolean);
              if (liveServiceNames.length > 0) {
                serviceNames = liveServiceNames;
              }
              setServices(allServices.filter((s) => (s?.Images || []).some((img) => img?.File_Url)));
              // อย่าทับที่อยู่เต็มที่ประกอบไว้แล้วด้วยชื่อสถานที่สั้นๆ จาก closeData
              serviceLocation =
                serviceLocation ||
                closeData?.ServiceAddr_Detail ||
                closeData?.Service_Addr ||
                closeData?.ServiceAddr_Name ||
                null;
              signatureInfo = {
                signed: !!(closeData?.HasSignature || closeData?.Round_Sign_Remark),
                signatureImage:
                  closeData?.SignatureBase64 && closeData?.SignatureMime
                    ? `data:${closeData.SignatureMime};base64,${closeData.SignatureBase64}`
                    : null,
                reason: closeData?.Round_Sign_Remark || null,
                signedBy: closeData?.Round_Sign_By || null,
                signedDate: closeData?.Round_Sign_Date || null,
              };
            } catch (_) {
              // optional section; do not block feedback page when signature is unavailable
            }
          }

          // fallback ซ้ำอีกรอบหลังจากรู้ contract_id จาก closeData แล้ว
          if ((!d.contract_no || !d.customer_name || !d.contract_name || !serviceLocation) && (d.contract_id || d.doc_id)) {
            try {
              const contractRefId = d.contract_id || d.doc_id;
              const contractRes = await api.get(`/get-contract/${contractRefId}`);
              const header = contractRes.data?.header || {};
              d.contract_no = d.contract_no || header.Doc_No || null;
              d.customer_name = d.customer_name || header.AR_Name || null;
              d.contract_name = d.contract_name || header.DisplayName || header.AR_Name || null;
              const addrParts = [
                header.AddrNo || null,
                header.Building || null,
                header.Village || null,
                header.Moo ? `หมู่ ${header.Moo}` : null,
                header.Soi ? `ซอย ${header.Soi}` : null,
                header.Road ? `ถนน ${header.Road}` : null,
                (header.SubDistrictName || header.SubDistrict) ? `แขวง/ตำบล ${header.SubDistrictName || header.SubDistrict}` : null,
                (header.DistrictName || header.District) ? `เขต/อำเภอ ${header.DistrictName || header.District}` : null,
                header.ProvinceName || header.Province || null,
                header.Zipcode || header.PostCode || null,
              ].filter(Boolean);
              const fullAddress = addrParts.length ? addrParts.join(" ") : null;
              const serviceAddrName = header.ServiceAddr_Name || serviceLocation || null;
              if (fullAddress) {
                serviceLocation = [serviceAddrName, fullAddress].filter(Boolean).join(" ").trim();
              } else {
                serviceLocation = serviceLocation || serviceAddrName;
              }
            } catch (_) {
              // ignore
            }
          }

          d.customer_signature = signatureInfo;
          d.service_names = serviceNames;
          d.service_location = serviceLocation;
          d.round_no = roundNo || d.round_no || null;
          d.total_rounds = totalRounds || d.total_rounds || null;
          setInfo(d);
          const criteria = d.criteria_ratings || {};
          const fallback = Number(d.rating || 0);
          setRatings({
            onTime: Number(criteria.on_time || fallback || 0),
            cleanliness: Number(criteria.cleanliness || fallback || 0),
            knowledge: Number(criteria.knowledge || fallback || 0),
            politeness: Number(criteria.politeness || fallback || 0),
          });
          if (d.is_submitted) {
            setComment(d.comment || "");
            setSubmitted(true);
          }
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (Object.values(ratings).some((v) => !v || v < 1 || v > 5)) {
      setError("กรุณาให้คะแนนทุกข้อ");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await api.post(`/app/public/feedback/${token}`, {
        rating: Math.round((ratings.onTime + ratings.cleanliness + ratings.knowledge + ratings.politeness) / 4),
        criteria_ratings: {
          on_time: ratings.onTime,
          cleanliness: ratings.cleanliness,
          knowledge: ratings.knowledge,
          politeness: ratings.politeness,
        },
        comment,
      });
      if (res.data?.status === "OK") {
        setSubmitted(true);
        setInfo((prev) => ({ ...prev, is_submitted: true }));
      } else {
        setError(res.data?.message || "เกิดข้อผิดพลาด");
      }
    } catch (e) {
      setError(e.response?.data?.message || "เกิดข้อผิดพลาด");
    }
    setSubmitting(false);
  };

  const generateAndUploadPdf = async () => {
    const calendarId = Number(info?.calendar_id);
    if (!calendarId) throw new Error("ยังไม่พบข้อมูลรอบงานสำหรับสร้าง PDF");

    const closeRes = await api.get("/get-job-close-web", { params: { calendar_id: calendarId } });
    const closeData = closeRes.data?.result;
    const services = Array.isArray(closeData?.Services) ? closeData.Services : [];
    const servicesWithImages = services.filter((srv) =>
      (srv?.Images || []).some((img) => img?.File_Url)
    );
    if (!servicesWithImages.length) throw new Error("ไม่พบรูปบริการสำหรับสร้าง PDF");

    const loadImg = (url) =>
      new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth;
          c.height = img.naturalHeight;
          c.getContext("2d").drawImage(img, 0, 0);
          resolve(c.toDataURL("image/jpeg", 0.9));
        };
        img.onerror = () => resolve(null);
        img.src = url;
      });

    const { jsPDF } = await import("jspdf");
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = 210;
    const pageH = 297;
    const margin = 14;
    const usableW = pageW - margin * 2;
    const gap = 3;
    const DPI = 300;
    const MM_TO_PX = DPI / 25.4;

    const makeTextImg = (text, fontSizePt, bold = false, align = "center") => {
      const fontSizePx = fontSizePt * (DPI / 72);
      const w = Math.round(usableW * MM_TO_PX);
      const h = Math.round(fontSizePx * 2.2);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = "#111111";
      ctx.font = `${bold ? "700 " : ""}${fontSizePx}px Sarabun, "Noto Sans Thai", Kanit, Arial, sans-serif`;
      ctx.textAlign = align;
      ctx.textBaseline = "middle";
      const x = align === "left" ? fontSizePx * 0.2 : align === "right" ? w - fontSizePx * 0.2 : w / 2;
      ctx.fillText(text, x, h / 2);
      return { dataUrl: canvas.toDataURL("image/png"), hMM: h / MM_TO_PX };
    };

    const addLine = (text, fontSizePt = 11, bold = false, align = "left") => {
      const img = makeTextImg(text, fontSizePt, bold, align);
      pdf.addImage(img.dataUrl, "PNG", margin, y, usableW, img.hMM);
      y += img.hMM + 1;
    };

    // ---- Page 1: สรุปผลการให้บริการ ----
    let y = margin;

    addLine("สรุปผลการให้บริการ", 20, true, "center");
    y += 3;

    if (info?.contract_no) {
      const roundText = info?.round_no && info?.total_rounds ? ` (บริการครั้งที่ ${info.round_no}/${info.total_rounds})` : "";
      addLine(`สัญญา: ${info.contract_no}${roundText}`, 11);
    }
    if (info?.customer_name) addLine(`ลูกค้า: ${info.customer_name}`, 11);
    if (info?.contract_name) addLine(`ชื่อในสัญญา: ${info.contract_name}`, 11);
    if (info?.service_location) addLine(`สถานที่: ${info.service_location}`, 10);
    if (Array.isArray(info?.service_names) && info.service_names.length)
      addLine(`บริการ: ${info.service_names.join(", ")}`, 11);
    if (info?.job_date) {
      const timeText = info?.time_start && info?.time_end ? ` เวลา ${info.time_start} – ${info.time_end}` : "";
      addLine(`วันที่บริการ: ${formatDate(info.job_date)}${timeText}`, 11);
    }
    if (info?.team_name) addLine(`ทีม: ${info.team_name}`, 11);
    if (comment) {
      y += 3;
      addLine("ความคิดเห็น:", 11, true);
      addLine(comment, 11);
    }

    // Signature
    const sigImg = info?.customer_signature?.signatureImage;
    if (sigImg) {
      y += 4;
      addLine("ลายเซ็นลูกค้า", 11, true, "center");
      const sigH = 40;
      const sigW = 80;
      const sigX = margin + (usableW - sigW) / 2;
      if (y + sigH > pageH - margin) { pdf.addPage(); y = margin; }
      pdf.addImage(sigImg, "PNG", sigX, y, sigW, sigH);
      y += sigH + 2;
    } else if (info?.customer_signature?.reason) {
      y += 3;
      addLine(`ลายเซ็น: ${info.customer_signature.reason}`, 11);
    }

    for (const srv of servicesWithImages) {
      const imgUrls = (srv?.Images || []).map((img) => img?.File_Url).filter(Boolean);
      const loadedImgs = await Promise.all(imgUrls.map(loadImg));
      const validImgs = loadedImgs.filter(Boolean);
      if (!validImgs.length) continue;
      const serviceMemo = String(srv?.Service_Memo || srv?.Service_Remark || srv?.Remark || "").trim();
      const useThreeColumnPattern = validImgs.length > 6;
      const renderServicePageHeader = () => {
        addLine(`บริการ: ${srv.Service_Name || "-"}`, 16, true, "center");
        if (serviceMemo) {
          addLine(`หมายเหตุ: ${serviceMemo}`, 11, false, "center");
        }
        y += 4;
      };
      const colW2 = (usableW - gap) / 2;
      const colW3 = (usableW - gap * 2) / 3;

      const drawTwoColumnPage = (imgs) => {
        const rowCount = Math.ceil(imgs.length / 2);
        const availableH = pageH - margin - y;
        const targetRowH = colW2 * 0.75;
        const maxRowH = (availableH - gap * (rowCount - 1)) / rowCount;
        const rowH = Math.min(targetRowH, maxRowH);
        imgs.forEach((img, i) => {
          const col = i % 2;
          const row = Math.floor(i / 2);
          const x = margin + col * (colW2 + gap);
          const yy = y + row * (rowH + gap);
          pdf.addImage(img, "JPEG", x, yy, colW2, rowH);
        });
      };

      const drawThreePatternPage = (imgs) => {
        const rowCounts = [];
        let remain = imgs.length;
        while (remain > 0) {
          if (remain >= 3) {
            rowCounts.push(3);
            remain -= 3;
          } else {
            rowCounts.push(remain);
            remain = 0;
          }
        }
        const availableH = pageH - margin - y;
        const targetRowH = colW3 * 0.75;
        const maxRowH = (availableH - gap * (rowCounts.length - 1)) / rowCounts.length;
        const rowH = Math.min(targetRowH, maxRowH);

        let imgIdx = 0;
        rowCounts.forEach((count, row) => {
          const width = count === 3 ? colW3 : colW2;
          const rowWidth = width * count + gap * (count - 1);
          const startX = count === 1 ? margin + (usableW - width) / 2 : margin + (usableW - rowWidth) / 2;
          const yy = y + row * (rowH + gap);
          for (let c = 0; c < count; c += 1) {
            const img = imgs[imgIdx++];
            const x = startX + c * (width + gap);
            pdf.addImage(img, "JPEG", x, yy, width, rowH);
          }
        });
      };

      let idx = 0;
      while (idx < validImgs.length) {
        pdf.addPage();
        y = margin;
        renderServicePageHeader();

        if (useThreeColumnPattern) {
          const pageImgs = validImgs.slice(idx, idx + 10);
          drawThreePatternPage(pageImgs);
          idx += pageImgs.length;
        } else {
          const pageImgs = validImgs.slice(idx, idx + 6);
          drawTwoColumnPage(pageImgs);
          idx += pageImgs.length;
        }
      }
    }

    const base64 = pdf.output("datauristring").split(",")[1];
    const uploadRes = await api.post(`/app/public/feedback/${token}/upload-pdf`, {
      pdf_base64: base64,
      filename: `service_pictures_feedback_${calendarId}_${Date.now()}.pdf`,
    });
    const generatedUrl = uploadRes.data?.file_url || null;
    if (!generatedUrl) throw new Error("สร้างไฟล์ PDF ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    setInfo((prev) => ({ ...prev, pdf_url: generatedUrl }));
    return generatedUrl;
  };

  const downloadFreshPdf = async () => {
    setError("");
    setLoadingPdf(true);
    try {
      const freshUrl = await generateAndUploadPdf();
      if (freshUrl) {
        window.open(freshUrl, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || "ไม่สามารถโหลดไฟล์ PDF ได้");
    } finally {
      setLoadingPdf(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <Icon icon="mdi:loading" className="w-10 h-10 animate-spin" />
          <span>กำลังโหลด...</span>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-500">
          <Icon icon="mdi:link-off" className="w-16 h-16 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium">ไม่พบข้อมูลการประเมิน</p>
          <p className="text-sm mt-1">Link นี้อาจไม่ถูกต้องหรือหมดอายุแล้ว</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 flex flex-col items-center p-4 gap-6 text-[14px] text-black [&_*]:text-[14px]">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">

        {/* Header */}
        <div className="bg-[#E13F57] text-white px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <h1 className="font-bold !text-white" style={{ fontSize: "32px", lineHeight: 1.2 }}>
              สรุปผลการให้บริการ
            </h1>
            {info.pdf_url ? (
              <button
                type="button"
                onClick={downloadFreshPdf}
                disabled={loadingPdf}
                className="inline-flex items-center justify-center text-white/90 hover:text-white transition-colors disabled:opacity-60 [&_svg]:!text-white"
                title="ดาวน์โหลด PDF"
              >
                <Icon
                  icon={loadingPdf ? "mdi:loading" : "mdi:download-circle-outline"}
                  className={`w-8 h-8 block mt-0.5 !text-white ${loadingPdf ? "animate-spin" : ""}`}
                  color="#fff"
                />
              </button>
            ) : (
              <button
                type="button"
                onClick={downloadFreshPdf}
                disabled={loadingPdf}
                className="inline-flex items-center justify-center text-white/90 hover:text-white transition-colors disabled:opacity-60 [&_svg]:!text-white"
                title="สร้างและดาวน์โหลด PDF"
              >
                <Icon
                  icon={loadingPdf ? "mdi:loading" : "mdi:download-circle-outline"}
                  className={`w-8 h-8 block mt-0.5 !text-white ${loadingPdf ? "animate-spin" : ""}`}
                  color="#fff"
                />
              </button>
            )}
          </div>
        </div>

        <div className="p-6 space-y-5">

          {/* Service Info */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm text-black">
            {info.contract_no && (
              <div className="flex items-center gap-2 text-black">
                <Icon icon="mdi:file-document-outline" className="w-4 h-4 text-black flex-shrink-0" />
                <span>
                  สัญญา: {info.contract_no}
                  {info.round_no && info.total_rounds ? ` (บริการครั้งที่ ${info.round_no}/${info.total_rounds})` : ""}
                </span>
              </div>
            )}
            {(info.customer_name || info.contract_name || info.service_location || (Array.isArray(info.service_names) && info.service_names.length > 0)) && (
              <div className="text-black space-y-1">
                {(info.customer_name || info.contract_name) && (
                  <>
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon icon="mdi:account" className="w-4 h-4 text-black flex-shrink-0" />
                      <span className="font-medium truncate">ชื่อลูกค้า: {info.customer_name || "-"}</span>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon icon="mdi:badge-account-outline" className="w-4 h-4 text-black flex-shrink-0" />
                      <span className="font-medium truncate">ชื่อตามสัญญา: {info.contract_name || "-"}</span>
                    </div>
                  </>
                )}
                {info.service_location && (
                  <div className="flex items-start gap-2 text-black">
                    <Icon icon="mdi:map-marker-outline" className="w-4 h-4 text-black flex-shrink-0 mt-0.5" />
                    <span>สถานที่ให้บริการ: {info.service_location}</span>
                  </div>
                )}
                {Array.isArray(info.service_names) && info.service_names.length > 0 && (
                  <div className="flex items-start gap-2 text-black">
                    <Icon icon="mdi:tools" className="w-4 h-4 text-black flex-shrink-0 mt-0.5" />
                    <span>บริการ: {info.service_names.join(", ")}</span>
                  </div>
                )}
              </div>
            )}
            {info.job_date && (
              <div className="flex items-center gap-2 text-black">
                <Icon icon="mdi:calendar" className="w-4 h-4 text-black flex-shrink-0" />
                <span>
                  วันที่บริการ: {formatDate(info.job_date)}
                  {info.time_start && info.time_end && ` เวลา ${info.time_start} – ${info.time_end}`}
                </span>
              </div>
            )}
            {info.team_name && (
              <div className="flex items-center gap-2 text-black">
                <Icon icon="mdi:account-group" className="w-4 h-4 text-black flex-shrink-0" />
                <span>ทีม: {info.team_name}</span>
              </div>
            )}
          </div>

          {/* Already submitted banner */}
          {submitted && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">
              <Icon icon="mdi:check-circle" className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">ขอบคุณสำหรับความคิดเห็น! ท่านได้ประเมินไปแล้ว</span>
            </div>
          )}

          {(info.customer_signature?.signatureImage || info.customer_signature?.reason) && (
            <div className="space-y-2 text-center">
              <p className="text-sm font-medium text-gray-700">ลายเซ็นลูกค้า</p>
              {info.customer_signature?.signatureImage ? (
                <div className="border rounded-xl bg-white p-2 flex justify-center">
                  <img
                    src={info.customer_signature.signatureImage}
                    alt="ลายเซ็นลูกค้า"
                    className="max-w-full max-h-40 object-contain"
                  />
                </div>
              ) : (
                <div className="text-sm text-gray-600 bg-gray-50 border rounded-xl px-3 py-2 text-center">
                  {info.customer_signature.reason}
                </div>
              )}
            </div>
          )}

          {/* Star Rating */}
          <div className="text-center space-y-2">
            <p className="text-sm font-medium text-black">ให้คะแนนการให้บริการ</p>
            <div className="space-y-3 text-left">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-black">1. ความตรงต่อเวลา</p>
                <StarRating value={ratings.onTime} onChange={(v) => setRatings((prev) => ({ ...prev, onTime: v }))} readonly={submitted} />
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-black">2. ความสะอาดของทีมช่าง</p>
                <StarRating value={ratings.cleanliness} onChange={(v) => setRatings((prev) => ({ ...prev, cleanliness: v }))} readonly={submitted} />
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-black">3. ความรู้ ความชำนาญ</p>
                <StarRating value={ratings.knowledge} onChange={(v) => setRatings((prev) => ({ ...prev, knowledge: v }))} readonly={submitted} />
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-black">4. ความสุภาพ มารยาท</p>
                <StarRating value={ratings.politeness} onChange={(v) => setRatings((prev) => ({ ...prev, politeness: v }))} readonly={submitted} />
              </div>
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 whitespace-nowrap">
              ความคิดเห็นเพิ่มเติม
            </label>
            <textarea
              rows={3}
              placeholder="แสดงความคิดเห็น (ไม่บังคับ)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={submitted}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-[#E13F57] focus:outline-none resize-none disabled:bg-gray-50 disabled:text-gray-500"
            />
          </div>

          {error && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <Icon icon="mdi:alert-circle" className="w-4 h-4" />
              {error}
            </p>
          )}

          {/* Submit */}
          {!submitted && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-[#E13F57] hover:bg-[#c7354a] text-white py-3 rounded-xl font-medium text-sm disabled:opacity-60 transition-colors"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Icon icon="mdi:loading" className="w-4 h-4 animate-spin" />
                  กำลังบันทึก...
                </span>
              ) : "ส่งแบบประเมิน"}
            </button>
          )}

        </div>
      </div>

      {/* Service image pages — one per service */}
      {services.map((srv, sIdx) => (
        <div key={sIdx} className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
          {(() => {
            const serviceMemo = String(srv?.Service_Memo || srv?.Service_Remark || srv?.Remark || "").trim();
            return (
              <>
          {/* Header */}
          <div className="bg-[#E13F57] text-white px-6 py-4">
            <p className="font-bold !text-white text-base leading-snug text-center">
              บริการ: {srv.Service_Name || "-"}
            </p>
            {serviceMemo && (
              <p className="!text-white/90 text-sm mt-1 break-words text-center">
                หมายเหตุ: {serviceMemo}
              </p>
            )}
          </div>
          {/* Image grid */}
          <div className="p-4 grid grid-cols-2 gap-2">
            {(srv.Images || [])
              .filter((img) => img?.File_Url)
              .map((img, iIdx) => (
                <img
                  key={iIdx}
                  src={img.File_Url}
                  alt={`รูปบริการ ${iIdx + 1}`}
                  className="w-full aspect-[4/3] object-cover rounded-lg bg-gray-100"
                />
              ))}
          </div>
              </>
            );
          })()}
        </div>
      ))}
    </div>
  );
}
